import React from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import styled from 'styled-components';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../../../constants.ts';
import { TandemPharmacyDetails } from '../../TandemPharmacyDetails.tsx';
import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import Hairline from 'design-system/src/components/Hairline/Hairline';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepsList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step-counter;
`;

const StepItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  counter-increment: step-counter;
`;

const StepTitle = styled(Typography)`
  &::before {
    content: counter(step-counter) '. ';
    font-weight: 600;
  }
`;

const PharmacyDetailsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`;

interface SendFirstPrescriptionModalProps {
  open: boolean;
  onClose: () => void;
}

const SendFirstPrescriptionModal: React.FC<SendFirstPrescriptionModalProps> = ({ open, onClose }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      fullHeight
      wide
      footerButtonConfig={{
        submitButton: {
          label: 'Done',
          onClick: onClose,
        },
      }}
    >
      <ModalTitle>Send your first prescription</ModalTitle>

      <ModalBody>
        <ContentContainer>
          <Hairline $marginTop="0" $marginBottom="0" />

          <StepsList>
            <StepItem>
              <StepTitle weight={600}>E-prescribe to Tandem within your EMR</StepTitle>
              <Typography color="var(--grayscale-600)">
                Tandem supports any prescription that requires a prior authorization (e.g. biologics, specialty
                medications, branded medications, and high-cost generics).
              </Typography>
              <PharmacyDetailsContainer>
                <TandemPharmacyDetails />
              </PharmacyDetailsContainer>
            </StepItem>

            <StepItem>
              <StepTitle weight={600}>
                Once we receive your prescription, track its progress in the "Prescriptions" tab in this portal
              </StepTitle>
            </StepItem>
          </StepsList>

          <StepContainer>
            <Typography weight={600}>Don't see Tandem in your EMR?</Typography>
            <Typography color="var(--grayscale-600)">
              {'Call us at '}
              {PROVIDER_SUPPORT_CONTACT_INFO.phone}
              {' or email '}
              <Button variant="link" href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`}>
                {PROVIDER_SUPPORT_CONTACT_INFO.email}
              </Button>
              {'.'}
            </Typography>
          </StepContainer>
        </ContentContainer>
      </ModalBody>
    </Modal>
  );
};

export default SendFirstPrescriptionModal;
